(function () {
  'use strict';

  var SOURCE = 'bxgy-gift';
  var PRIVATE = {
    gift: '_bxgy_gift',
    ruleId: '_bxgy_rule_id',
    parentVariantId: '_bxgy_parent_variant_id',
    giftVariantId: '_bxgy_gift_variant_id',
    buyQuantity: '_bxgy_buy_quantity',
    giftQuantity: '_bxgy_gift_quantity'
  };

  var isSyncing = false;
  var syncQueued = false;
  var syncTimer = null;
  var ruleBoundaryTimer = null;
  var activeSyncPromise = null;
  var retryCount = 0;
  var maxRetries = 2;
  var syncBlockedUntil = 0;
  var maxTimerDelay = 2147483647;
  var ruleBoundaryJitter = 5000;
  var lastCartErrorMessage = '';
  var RULE_CACHE_KEY = 'bxgy-gift-rules-cache-v1';

  window.theme = window.theme || {};

  function route(name, fallback) {
    return window.theme && window.theme.routes && window.theme.routes[name] ? window.theme.routes[name] : fallback;
  }

  function withSource(url) {
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'source=' + SOURCE;
  }

  function normalizeId(value) {
    if (value === null || value === undefined) return '';
    var id = String(value).trim();
    if (!id) return '';
    return id.split('/').pop();
  }

  function toInt(value, fallback) {
    var parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  }

  function positiveInt(value, fallback) {
    var parsed = toInt(value, fallback);
    return parsed > 0 ? parsed : fallback;
  }

  function firstDefined() {
    for (var index = 0; index < arguments.length; index += 1) {
      if (arguments[index] !== undefined && arguments[index] !== null) return arguments[index];
    }

    return null;
  }

  // Parses one optional rule date into a comparable timestamp.
  function toTimestamp(value) {
    if (value === null || value === undefined) return null;

    var text = String(value).trim();
    if (!text) return null;

    var parsed = Date.parse(text);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function parseJsonValue(value) {
    if (!value) return null;
    if (typeof value !== 'string') return value;

    try {
      var parsed = JSON.parse(value);
      if (typeof parsed === 'string') {
        return parseJsonValue(parsed);
      }
      return parsed;
    } catch (error) {
      console.warn('[BXGY] Invalid rule JSON', error);
      return null;
    }
  }

  function collectRuleConfig(config, rules) {
    var parsed = parseJsonValue(config);
    if (!parsed) return;

    if (Array.isArray(parsed)) {
      parsed.forEach(function (rule) {
        rules.push(rule);
      });
      return;
    }

    if (Array.isArray(parsed.rules)) {
      parsed.rules.forEach(function (rule) {
        rules.push(rule);
      });
      return;
    }

    rules.push(parsed);
  }

  // Reads the last known BXGY rule payloads so cart sync can keep working after
  // a full page navigation where the next page only renders a subset of rules.
  function readCachedRuleConfig() {
    try {
      if (!window.sessionStorage) return [];

      var cached = parseJsonValue(window.sessionStorage.getItem(RULE_CACHE_KEY));
      return Array.isArray(cached) ? cached : [];
    } catch (error) {
      return [];
    }
  }

  function rawRuleId(rule) {
    if (!rule || typeof rule !== 'object') return '';

    return String(firstDefined(
      rule.ruleId,
      rule.rule_id,
      rule.id,
      rule.system && (rule.system.handle || rule.system.id)
    ) || '').trim();
  }

  // Keeps one merged rule list in session storage and lets the latest payload win.
  function mergeRuleConfig(cachedRules, currentRules) {
    var mergedRules = [];

    (Array.isArray(cachedRules) ? cachedRules : []).forEach(function (rule) {
      mergedRules.push(rule);
    });

    (Array.isArray(currentRules) ? currentRules : []).forEach(function (rule) {
      mergedRules.push(rule);
    });

    var seenRuleIds = {};
    return mergedRules.slice().reverse().filter(function (rule) {
      var identifier = rawRuleId(rule);

      if (!identifier) return true;
      if (seenRuleIds[identifier]) return false;

      seenRuleIds[identifier] = true;
      return true;
    }).reverse();
  }

  function writeCachedRuleConfig(rules) {
    try {
      if (!window.sessionStorage) return;
      window.sessionStorage.setItem(RULE_CACHE_KEY, JSON.stringify(Array.isArray(rules) ? rules : []));
    } catch (error) {
      return;
    }
  }

  function normalizeIds(value) {
    var values = Array.isArray(value) ? value : String(value || '').split(',');
    return values.map(normalizeId).filter(Boolean);
  }

  function normalizeVariantItems(rawItems, rawIds, fallbackQuantity) {
    var fallback = positiveInt(fallbackQuantity, 1);
    var items = Array.isArray(rawItems) ? rawItems.map(function (item) {
      var variantId = normalizeId(item && (item.variantId || item.id));
      if (!variantId) return null;

      return {
        variantId: variantId,
        quantity: positiveInt(item.quantity, fallback)
      };
    }).filter(Boolean) : [];

    if (!items.length) {
      items = normalizeIds(rawIds).map(function (variantId) {
        return { variantId: variantId, quantity: fallback };
      });
    }

    var seen = {};
    return items.filter(function (item) {
      if (seen[item.variantId]) return false;
      seen[item.variantId] = true;
      return true;
    });
  }

  function enumValue(value, allowedValues, fallback) {
    var normalized = String(value || '').trim().toUpperCase();
    return allowedValues.indexOf(normalized) !== -1 ? normalized : fallback;
  }

  function objectValue(value) {
    return value && typeof value === 'object' ? value : {};
  }

  // Normalizes one or more configured gift variants into comparable ids.
  function normalizeGiftVariantIds(rule) {
    var rawGiftIds = rule.giftVariantIds || rule.gift_variant_ids || rule.giftVariants || rule.gift_variants;

    if (Array.isArray(rawGiftIds)) {
      return rawGiftIds.map(normalizeId).filter(Boolean);
    }

    if (rawGiftIds !== undefined && rawGiftIds !== null && String(rawGiftIds).trim() !== '') {
      return String(rawGiftIds).split(',').map(normalizeId).filter(Boolean);
    }

    var singleGiftId = normalizeId(rule.giftVariantId || rule.gift_variant_id || rule.giftVariant || rule.gift_variant);
    return singleGiftId ? [singleGiftId] : [];
  }

  function normalizeRule(rule, originalIndex) {
    if (!rule || rule.enabled === false || rule.enabled === 'false') return null;

    var trigger = objectValue(rule.trigger);
    var gift = objectValue(rule.gift);
    var isV2 = Boolean(rule.trigger && rule.gift);
    var triggerMode = enumValue(trigger.mode, ['ANY', 'ALL'], 'ANY');
    var quantityMode = enumValue(trigger.quantityMode, ['EACH', 'SET', 'COMBINED'], triggerMode === 'ALL' ? 'SET' : 'EACH');
    var combinedQuantity = quantityMode === 'COMBINED'
      ? positiveInt(trigger.combinedQuantity || trigger.buyQuantity || rule.buyQuantity || rule.buy_quantity, 1)
      : null;
    var rawMainIds = rule.mainVariantIds || rule.main_variant_ids || rule.mainVariants || rule.main_variant_id || rule.mainVariantId;
    var triggerItems = isV2
      ? normalizeVariantItems(trigger.items, trigger.variantIds, quantityMode === 'COMBINED' ? 1 : trigger.buyQuantity)
      : normalizeVariantItems([], rawMainIds, rule.buyQuantity || rule.buy_quantity);
    var giftItems = isV2
      ? normalizeVariantItems(gift.items, gift.variantIds, gift.quantity)
      : normalizeVariantItems([], normalizeGiftVariantIds(rule), rule.giftQuantity || rule.gift_quantity);
    var mainVariantIds = triggerItems.map(function (item) { return item.variantId; });
    var giftVariantIds = giftItems.map(function (item) { return item.variantId; });

    if (quantityMode === 'COMBINED' && triggerMode === 'ALL') {
      combinedQuantity = Math.max(combinedQuantity, triggerItems.length);
    }

    if (!giftVariantIds.length || !mainVariantIds.length) return null;

    return {
      version: isV2 ? 2 : 1,
      ruleId: String(rule.ruleId || rule.rule_id || rule.id || [mainVariantIds.join('-'), giftVariantIds.join('-')].join(':')),
      mainVariantIds: mainVariantIds,
      giftVariantIds: giftVariantIds,
      triggerItems: triggerItems,
      giftItems: giftItems,
      buyQuantity: quantityMode === 'COMBINED'
        ? combinedQuantity
        : (triggerItems[0] ? triggerItems[0].quantity : positiveInt(rule.buyQuantity || rule.buy_quantity, 1)),
      combinedQuantity: combinedQuantity,
      giftQuantity: giftItems[0] ? giftItems[0].quantity : positiveInt(rule.giftQuantity || rule.gift_quantity, 1),
      startsAt: toTimestamp(rule.startsAt || rule.starts_at),
      endsAt: toTimestamp(rule.endsAt || rule.ends_at),
      updatedAt: toTimestamp(rule.updatedAt || rule.updated_at || rule.system && (rule.system.updatedAt || rule.system.updated_at)),
      triggerMode: triggerMode,
      quantityMode: quantityMode,
      originalIndex: originalIndex
    };
  }

  function getRules() {
    var rawRules = [];
    var settingsRules = window.theme && window.theme.settings ? window.theme.settings.bxgyGiftRules : null;
    var cachedRules = readCachedRuleConfig();

    collectRuleConfig(settingsRules, rawRules);

    document.querySelectorAll('[data-bxgy-rules]').forEach(function (node) {
      collectRuleConfig(node.textContent, rawRules);
    });

    document.querySelectorAll('[data-bxgy-rule]').forEach(function (node) {
      collectRuleConfig(node.textContent, rawRules);
    });

    rawRules = mergeRuleConfig(cachedRules, rawRules);
    writeCachedRuleConfig(rawRules);

    var normalizedRules = rawRules.map(function (rule, index) {
      return normalizeRule(rule, index);
    }).filter(Boolean);

    // Product pages can render the same rule payload more than once, so collapse
    // duplicates by rule id and keep the latest occurrence.
    var seenRuleIds = {};
    return normalizedRules.slice().reverse().filter(function (rule) {
      if (seenRuleIds[rule.ruleId]) return false;
      seenRuleIds[rule.ruleId] = true;
      return true;
    }).reverse();
  }

  // Returns whether one BXGY rule is active for the current time window.
  function isRuleActiveAt(rule, now) {
    if (rule.startsAt !== null && now < rule.startsAt) return false;
    if (rule.endsAt !== null && now > rule.endsAt) return false;
    return true;
  }

  // Schedules a cart sync at the next rule start or end boundary.
  function scheduleRuleBoundarySync(rules) {
    window.clearTimeout(ruleBoundaryTimer);

    rules = Array.isArray(rules) ? rules : getRules();
    if (!rules.length) return;

    var now = Date.now();
    var nextBoundary = null;

    rules.forEach(function (rule) {
      [rule.startsAt, rule.endsAt].forEach(function (timestamp) {
        if (timestamp === null || timestamp <= now) return;
        if (nextBoundary === null || timestamp < nextBoundary) {
          nextBoundary = timestamp;
        }
      });
    });

    if (nextBoundary === null) return;

    ruleBoundaryTimer = window.setTimeout(function () {
      ruleBoundaryTimer = null;
      requestSync();
      scheduleRuleBoundarySync(getRules());
    }, Math.min(nextBoundary - now + 250 + Math.floor(Math.random() * ruleBoundaryJitter), maxTimerDelay));
  }

  // Breaks same-variant rule ties by most recent update timestamp.
  function compareRuleRecency(leftRule, rightRule) {
    var leftUpdatedAt = leftRule.updatedAt === null ? -1 : leftRule.updatedAt;
    var rightUpdatedAt = rightRule.updatedAt === null ? -1 : rightRule.updatedAt;

    if (leftUpdatedAt !== rightUpdatedAt) {
      return rightUpdatedAt - leftUpdatedAt;
    }

    return rightRule.originalIndex - leftRule.originalIndex;
  }

  // Returns the single effective BXGY rule that applies to a main product variant.
  function findRulesForVariant(variantId, rules) {
    var normalizedVariantId = normalizeId(variantId);
    if (!normalizedVariantId) return [];

    var availableRules = Array.isArray(rules) ? rules : getRules();

    var matchingRules = availableRules.filter(function (rule) {
      return rule.giftVariantIds.indexOf(normalizedVariantId) === -1 && rule.mainVariantIds.indexOf(normalizedVariantId) !== -1;
    });
    var activeRules = matchingRules.filter(function (rule) {
      return isRuleActiveAt(rule, Date.now());
    });

    if (!activeRules.length) return [];
    if (activeRules.length === 1) return activeRules;

    return [activeRules.slice().sort(compareRuleRecency)[0]];
  }

  // Preserves the original single-rule helper for existing callers.
  function findRuleForVariant(variantId) {
    return findRulesForVariant(variantId)[0] || null;
  }

  function eligibleGiftQuantity(mainQuantity, rule) {
    var quantity = positiveInt(mainQuantity, 1);
    return Math.floor(quantity / rule.buyQuantity) * rule.giftQuantity;
  }

  function giftQuantityForVariant(rule, giftVariantId) {
    var item = (rule.giftItems || []).find(function (giftItem) {
      return giftItem.variantId === normalizeId(giftVariantId);
    });

    return item ? item.quantity : rule.giftQuantity;
  }

  function effectiveGiftQuantityForVariant(rule, giftVariantId, requestedQuantity) {
    var normalizedQuantity = Math.max(toInt(requestedQuantity, 0), 0);
    if (normalizedQuantity <= 0) return 0;

    return normalizedQuantity;
  }

  // Estimates how many gifts a PDP add-to-cart attempt would need for one rule.
  function giftQuantityForMainItem(mainItem, rule) {
    if (!mainItem || !rule) return 0;
    if (isInlineGiftRule(rule)) return eligibleGiftQuantity(mainItem.quantity, rule);

    var mainVariantId = normalizeId(mainItem.id);
    var mainQuantity = positiveInt(mainItem.quantity, 1);
    var triggerItem = (rule.triggerItems || []).find(function (item) {
      return item.variantId === mainVariantId;
    });

    if (!triggerItem) return 0;
    if (rule.triggerMode === 'ALL' && (rule.triggerItems || []).length > 1) return 0;
    if (rule.quantityMode === 'COMBINED') return Math.floor(mainQuantity / positiveInt(rule.combinedQuantity || rule.buyQuantity, 1));
    return Math.floor(mainQuantity / positiveInt(triggerItem.quantity, 1));
  }

  function validateGiftStockForRules(mainItem, rules) {
    return [];
  }

  function isInlineGiftRule(rule) {
    return rule.version !== 2;
  }

  function decorateMainItem(item, rule) {
    if (!item || !rule) return item;

    item.properties = Object.assign({}, item.properties || {}, {
      _bxgy_rule_id: rule.ruleId,
      _bxgy_gift_variant_id: rule.giftVariantIds[0] || '',
      _bxgy_buy_quantity: String(rule.buyQuantity),
      _bxgy_gift_quantity: String(rule.giftQuantity)
    });

    return item;
  }

  // Builds one gift line per selected gift variant on the BXGY rule.
  function buildGiftItems(mainItem, rule, quantityOverride) {
    if (!mainItem || !rule) return [];
    if (!isInlineGiftRule(rule) && quantityOverride === undefined) return [];

    var parentVariantId = normalizeId(mainItem.id);
    var quantity = quantityOverride !== undefined ? quantityOverride : eligibleGiftQuantity(mainItem.quantity, rule);
    if (!parentVariantId || quantity <= 0) return [];
    var giftVariantIds = rule.giftVariantIds;

    return giftVariantIds.map(function (giftVariantId) {
      var effectiveQuantity = effectiveGiftQuantityForVariant(rule, giftVariantId, quantity);
      if (effectiveQuantity <= 0) return null;

      return {
        id: giftVariantId,
        quantity: effectiveQuantity,
        properties: {
          _source: SOURCE,
          _bxgy_gift: 'true',
          _bxgy_rule_id: rule.ruleId,
          _bxgy_parent_variant_id: parentVariantId,
          _bxgy_gift_variant_id: giftVariantId,
          _bxgy_buy_quantity: String(rule.buyQuantity),
          _bxgy_gift_quantity: String(giftQuantityForVariant(rule, giftVariantId))
        }
      };
    }).filter(Boolean);
  }

  function buildGiftItem(mainItem, rule, quantityOverride) {
    return buildGiftItems(mainItem, rule, quantityOverride)[0] || null;
  }

  function isBxgyGiftLine(item) {
    return Boolean(item && item.properties && item.properties[PRIVATE.gift] === 'true');
  }

  function groupKey(ruleId, parentVariantId, giftVariantId) {
    return [ruleId, normalizeId(parentVariantId), normalizeId(giftVariantId)].join('|');
  }

  function ruleGiftKey(ruleId, giftVariantId) {
    return [ruleId, normalizeId(giftVariantId)].join('|');
  }

  function getCart() {
    return fetch(route('cart_url', '/cart') + '.js', {
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('Cart fetch failed: ' + response.status);
      return response.json();
    });
  }

  function parseJsonResponse(response) {
    return response.text().then(function (text) {
      if (!text) return null;

      try {
        return JSON.parse(text);
      } catch (error) {
        return null;
      }
    });
  }

  function payloadMessage(payload, fallback) {
    var message = payload && payload.message ? String(payload.message).trim() : '';
    var description = payload && payload.description ? payload.description : '';

    if (description && typeof description === 'object') {
      description = Object.keys(description).map(function (key) {
        return String(description[key] || '').trim();
      }).filter(Boolean).join(' ');
    } else {
      description = String(description || '').trim();
    }

    if (message && description && message !== description) return message + ': ' + description;
    if (description) return description;
    if (message) return message;

    return fallback;
  }

  function buildCartRequestError(response, payload, fallback) {
    var error = new Error(payloadMessage(payload, fallback));
    error.name = 'CartRequestError';
    error.status = response.status;
    error.statusText = response.statusText;
    error.payload = payload || null;
    error.retryable = response.status === 429 || response.status >= 500;

    return error;
  }

  function parseCartMutationResponse(response, fallback) {
    return parseJsonResponse(response).then(function (payload) {
      if (!response.ok) {
        throw buildCartRequestError(response, payload, fallback);
      }

      return payload || {};
    });
  }

  function postCartUpdate(updates) {
    return fetch(withSource(route('cart_update_url', '/cart/update')), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ updates: updates })
    }).then(function (response) {
      return parseCartMutationResponse(response, 'Cart update failed: ' + response.status);
    });
  }

  function postCartAdd(items) {
    return fetch(withSource(route('cart_add_url', '/cart/add') + '.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      return parseCartMutationResponse(response, 'Cart add failed: ' + response.status);
    });
  }

  function postCartAddIndependently(items) {
    var result = {
      added: [],
      errors: []
    };

    return items.reduce(function (promise, item) {
      return promise.then(function () {
        return postCartAdd([item]).then(function (payload) {
          result.added.push({ item: item, payload: payload });
        }).catch(function (error) {
          result.errors.push({ item: item, error: error });
          console.warn('[BXGY] Gift add skipped', error);
        });
      });
    }, Promise.resolve()).then(function () {
      if (!result.added.length && result.errors.length) {
        throw result.errors[0].error;
      }

      return result;
    });
  }

  function postCartAddResilient(items) {
    if (!Array.isArray(items) || !items.length) {
      return Promise.resolve({ added: [], errors: [] });
    }

    return postCartAdd(items).then(function (payload) {
      return {
        added: items.map(function (item) {
          return { item: item, payload: payload };
        }),
        errors: []
      };
    }).catch(function (error) {
      if (!error || error.retryable !== false || items.length <= 1) {
        throw error;
      }

      return postCartAddIndependently(items);
    });
  }

  // Builds BXGY main/gift group state from cart contents, allowing one main variant
  // to contribute to multiple gift rules at the same time.
  function collectCartState(cart, rules) {
    var groups = {};
    var giftLines = {};
    var v2GiftLines = {};
    var mainQuantities = {};
    var availableRules = Array.isArray(rules) ? rules : getRules();
    var rulesById = {};

    availableRules.forEach(function (rule) {
      rulesById[rule.ruleId] = rule;
    });

    (cart.items || []).forEach(function (item) {
      var properties = item.properties || {};

      if (isBxgyGiftLine(item)) {
        var giftRule = rulesById[properties[PRIVATE.ruleId]];

        if (giftRule && giftRule.version === 2) {
          var v2GiftKey = ruleGiftKey(properties[PRIVATE.ruleId], item.variant_id || item.id);
          v2GiftLines[v2GiftKey] = v2GiftLines[v2GiftKey] || [];
          v2GiftLines[v2GiftKey].push(item);
        } else {
          var giftKey = groupKey(properties[PRIVATE.ruleId], properties[PRIVATE.parentVariantId], item.variant_id || item.id);
          giftLines[giftKey] = giftLines[giftKey] || [];
          giftLines[giftKey].push(item);
        }

        return;
      }

      var parentVariantId = normalizeId(item.variant_id || item.id);
      if (!parentVariantId) return;

      mainQuantities[parentVariantId] = (mainQuantities[parentVariantId] || 0) + toInt(item.quantity, 0);

      findRulesForVariant(parentVariantId, availableRules).forEach(function (rule) {
        if (rule.version === 2) return;

        rule.giftVariantIds.forEach(function (giftVariantId) {
          var key = groupKey(rule.ruleId, parentVariantId, giftVariantId);
          var giftItem = (rule.giftItems || []).find(function (item) {
            return item.variantId === normalizeId(giftVariantId);
          }) || {};

          groups[key] = groups[key] || {
            ruleId: rule.ruleId,
            parentVariantId: parentVariantId,
            giftVariantId: giftVariantId,
            mainQuantity: 0,
            buyQuantity: rule.buyQuantity,
            giftQuantity: rule.giftQuantity
          };

          groups[key].mainQuantity += toInt(item.quantity, 0);
        });
      });
    });

    return {
      groups: groups,
      giftLines: giftLines,
      v2GiftLines: v2GiftLines,
      mainQuantities: mainQuantities
    };
  }

  function v2TriggerCount(rule, quantities) {
    var items = (rule.triggerItems || []).map(function (item) {
      return {
        requiredQuantity: positiveInt(item.quantity, 1),
        actualQuantity: toInt(quantities[item.variantId], 0)
      };
    });
    if (!items.length) return 0;

    if (rule.triggerMode === 'ALL' && items.some(function (item) { return item.actualQuantity <= 0; })) return 0;

    if (rule.quantityMode === 'SET') {
      if (items.some(function (item) { return item.actualQuantity <= 0; })) return 0;
      return Math.min.apply(null, items.map(function (item) {
        return Math.floor(item.actualQuantity / item.requiredQuantity);
      }));
    }

    if (rule.quantityMode === 'COMBINED') {
      // Combined rules use one shared threshold across all selected trigger variants.
      // Do not sum per-item required quantities again or the rule will over-fire.
      var totalRequired = positiveInt(rule.combinedQuantity || rule.buyQuantity, items.reduce(function (sum, item) {
        return sum + item.requiredQuantity;
      }, 0));
      var totalActual = items.reduce(function (sum, item) { return sum + item.actualQuantity; }, 0);
      return totalRequired > 0 ? Math.floor(totalActual / totalRequired) : 0;
    }

    if (rule.triggerMode === 'ALL') {
      return Math.min.apply(null, items.map(function (item) {
        return Math.floor(item.actualQuantity / item.requiredQuantity);
      }));
    }

    return items.reduce(function (sum, item) {
      return sum + Math.floor(item.actualQuantity / item.requiredQuantity);
    }, 0);
  }

  function buildV2GiftItem(rule, giftVariantId, quantity) {
    return {
      id: giftVariantId,
      quantity: quantity,
      properties: {
        _source: SOURCE,
        _bxgy_gift: 'true',
        _bxgy_rule_id: rule.ruleId,
        _bxgy_parent_variant_id: '',
        _bxgy_gift_variant_id: giftVariantId,
        _bxgy_buy_quantity: String(rule.buyQuantity),
        _bxgy_gift_quantity: String(giftQuantityForVariant(rule, giftVariantId))
      }
    };
  }

  // Re-adds an unqualified gift as a normal cart line so it no longer receives
  // the BXGY discount after its trigger is gone.
  function buildRegularCartItem(variantId, quantity) {
    return {
      id: variantId,
      quantity: quantity
    };
  }

  // Removes BXGY private-property gift lines and keeps their quantity as paid items.
  function restoreGiftLines(gifts, updates, additions) {
    var quantitiesByVariantId = {};

    gifts.forEach(function (gift) {
      var giftVariantId = normalizeId(gift.variant_id || gift.id);
      var giftQuantity = toInt(gift.quantity, 0);

      if (gift.key) {
        updates[gift.key] = 0;
      }

      if (!giftVariantId || giftQuantity <= 0) return;
      quantitiesByVariantId[giftVariantId] = (quantitiesByVariantId[giftVariantId] || 0) + giftQuantity;
    });

    Object.keys(quantitiesByVariantId).forEach(function (giftVariantId) {
      additions.push(buildRegularCartItem(giftVariantId, quantitiesByVariantId[giftVariantId]));
    });
  }

  // Rebuilds the minimum v1 rule state from one BXGY gift line when the current
  // page no longer has the original rule JSON in the DOM.
  function fallbackGroupFromGiftLines(gifts, mainQuantities) {
    var firstGift = Array.isArray(gifts) && gifts.length ? gifts[0] : null;
    var properties = firstGift && firstGift.properties ? firstGift.properties : {};
    var parentVariantId = normalizeId(properties[PRIVATE.parentVariantId]);
    var giftVariantId = normalizeId(firstGift && (firstGift.variant_id || firstGift.id || properties[PRIVATE.giftVariantId]));
    var ruleId = String(properties[PRIVATE.ruleId] || '').trim();
    var mainQuantity = toInt(mainQuantities[parentVariantId], 0);
    var buyQuantity = positiveInt(properties[PRIVATE.buyQuantity], 1);
    var giftQuantity = positiveInt(properties[PRIVATE.giftQuantity], 1);

    if (!ruleId || !parentVariantId || !giftVariantId || mainQuantity <= 0) return null;

    return {
      ruleId: ruleId,
      parentVariantId: parentVariantId,
      giftVariantId: giftVariantId,
      mainQuantity: mainQuantity,
      buyQuantity: buyQuantity,
      giftQuantity: giftQuantity
    };
  }

  function reduceGiftLines(gifts, targetQuantity, updates) {
    var remaining = targetQuantity;

    gifts.forEach(function (gift) {
      var currentQuantity = toInt(gift.quantity, 0);
      var nextQuantity = Math.min(currentQuantity, remaining);

      if (nextQuantity !== currentQuantity) {
        updates[gift.key] = nextQuantity;
      }

      remaining -= nextQuantity;
      if (remaining < 0) remaining = 0;
    });
  }

  function buildSyncPlan(cart) {
    var rules = getRules();
    if (!rules.length) {
      return {
        updates: {},
        additions: []
      };
    }

    var state = collectCartState(cart, rules);
    var groups = state.groups;
    var giftLines = state.giftLines;
    var v2GiftLines = state.v2GiftLines;
    var updates = {};
    var additions = [];
    var v2TargetKeys = {};

    Object.keys(groups).forEach(function (key) {
      var group = groups[key];
      var targetGiftQuantity = Math.floor(group.mainQuantity / group.buyQuantity) * group.giftQuantity;
      var groupRule = {
        ruleId: group.ruleId,
        mainVariantIds: [group.parentVariantId],
        giftVariantIds: [group.giftVariantId],
        giftItems: [{
          variantId: group.giftVariantId,
          quantity: group.giftQuantity
        }],
        buyQuantity: group.buyQuantity,
        giftQuantity: group.giftQuantity
      };
      var gifts = giftLines[key] || [];
      var currentGiftQuantity = gifts.reduce(function (sum, gift) {
        return sum + toInt(gift.quantity, 0);
      }, 0);
      var effectiveGiftQuantity = targetGiftQuantity;


      if (effectiveGiftQuantity === currentGiftQuantity) return;

      if (effectiveGiftQuantity > currentGiftQuantity) {
        additions.push(buildGiftItem(
          { id: group.parentVariantId, quantity: 1 },
          groupRule,
          effectiveGiftQuantity - currentGiftQuantity
        ));
        return;
      }

      reduceGiftLines(gifts, effectiveGiftQuantity, updates);
    });

    rules.filter(function (rule) {
      return rule.version === 2 && isRuleActiveAt(rule, Date.now());
    }).forEach(function (rule) {
      var triggerCount = v2TriggerCount(rule, state.mainQuantities);

      rule.giftVariantIds.forEach(function (giftVariantId) {
        var key = ruleGiftKey(rule.ruleId, giftVariantId);
        v2TargetKeys[key] = true;
        var targetGiftQuantity = triggerCount * giftQuantityForVariant(rule, giftVariantId);
        var gifts = v2GiftLines[key] || [];
        var currentGiftQuantity = gifts.reduce(function (sum, gift) {
          return sum + toInt(gift.quantity, 0);
        }, 0);
        var effectiveGiftQuantity = targetGiftQuantity;

        if (effectiveGiftQuantity === currentGiftQuantity) return;

        if (effectiveGiftQuantity > currentGiftQuantity) {
          additions.push(buildV2GiftItem(rule, giftVariantId, effectiveGiftQuantity - currentGiftQuantity));
          return;
        }

        reduceGiftLines(gifts, effectiveGiftQuantity, updates);
      });
    });

    Object.keys(v2GiftLines).forEach(function (key) {
      if (v2TargetKeys[key]) return;

      // If the rule payload is unavailable on the current page, keeping the
      // existing gift line is safer than converting it into a paid item.
      return;
    });

    Object.keys(giftLines).forEach(function (key) {
      if (groups[key]) return;

      var gifts = giftLines[key] || [];
      var fallbackGroup = fallbackGroupFromGiftLines(gifts, state.mainQuantities);

      if (fallbackGroup) {
        var fallbackTargetGiftQuantity = Math.floor(fallbackGroup.mainQuantity / fallbackGroup.buyQuantity) * fallbackGroup.giftQuantity;
        var fallbackCurrentGiftQuantity = gifts.reduce(function (sum, gift) {
          return sum + toInt(gift.quantity, 0);
        }, 0);

        if (fallbackTargetGiftQuantity === fallbackCurrentGiftQuantity) return;

        if (fallbackTargetGiftQuantity > fallbackCurrentGiftQuantity) {
          additions.push(buildGiftItem(
            { id: fallbackGroup.parentVariantId, quantity: 1 },
            {
              ruleId: fallbackGroup.ruleId,
              giftVariantIds: [fallbackGroup.giftVariantId],
              giftItems: [{ variantId: fallbackGroup.giftVariantId, quantity: fallbackGroup.giftQuantity }],
              buyQuantity: fallbackGroup.buyQuantity,
              giftQuantity: fallbackGroup.giftQuantity,
              version: 1
            },
            fallbackTargetGiftQuantity - fallbackCurrentGiftQuantity
          ));
          return;
        }

        reduceGiftLines(gifts, fallbackTargetGiftQuantity, updates);
        return;
      }

      // Without the owning rule payload we cannot prove this gift should be
      // converted into a paid item, so leave it unchanged for a later sync.
      return;
    });

    return {
      updates: updates,
      additions: additions.filter(Boolean)
    };
  }

  function hasObjectKeys(object) {
    return Object.keys(object).length > 0;
  }

  // Parse cart request payloads so we can avoid scheduling a redundant BXGY sync
  // when the theme already submitted the main item and gift in one add-to-cart call.
  function parseRequestBody(config) {
    if (!config || typeof config.body !== 'string') return null;

    try {
      return JSON.parse(config.body);
    } catch (error) {
      return null;
    }
  }

  function requestContainsBxgyItems(config) {
    var payload = parseRequestBody(config);
    var items = Array.isArray(payload && payload.items) ? payload.items : [];

    return items.some(function (item) {
      var properties = item && item.properties ? item.properties : {};
      return Boolean(properties[PRIVATE.ruleId] || properties[PRIVATE.gift] === 'true' || properties[PRIVATE.parentVariantId]);
    });
  }

  function dispatchCartRefresh() {
    document.dispatchEvent(new CustomEvent('cart:drawer:update'));
    document.dispatchEvent(new CustomEvent('bxgy:cart:synced'));
  }

  // Persists the most recent BXGY cart error so late-mounted cart UIs can still show it.
  function setLastCartErrorMessage(message) {
    lastCartErrorMessage = String(message || '').trim();
  }

  // BXGY gift sync failures should never interrupt or warn the customer.
  function dispatchCartError(error) {
    setLastCartErrorMessage('');
    console.warn('[BXGY] Gift sync issue ignored', error);
  }

  function runSync() {
    if (isSyncing) {
      syncQueued = true;
      return activeSyncPromise || Promise.resolve();
    }

    isSyncing = true;
    var syncPlan = null;

    activeSyncPromise = getCart()
      .then(function (cart) {
        var plan = buildSyncPlan(cart);
        syncPlan = plan;
        var promise = Promise.resolve();

        if (hasObjectKeys(plan.updates)) {
          promise = promise.then(function () {
            return postCartUpdate(plan.updates);
          });
        }

        if (plan.additions.length) {
          promise = promise.then(function () {
            return postCartAddResilient(plan.additions);
          });
        }

        return promise.then(function () {
          setLastCartErrorMessage('');

          retryCount = 0;
          syncBlockedUntil = 0;
          if (hasObjectKeys(plan.updates) || plan.additions.length) {
            dispatchCartRefresh();
          }

          return { ok: true };
        });
      })
      .catch(function (error) {
        if (syncPlan && syncPlan.additions && syncPlan.additions.length) {
          dispatchCartError(error);
        }

        var isRateLimited = Boolean(error && (error.status === 429 || String(error.message || '').indexOf('429') !== -1));
        if (isRateLimited) {
          syncBlockedUntil = Date.now() + 1500;
        }

        if (error && error.retryable === false) {
          retryCount = 0;
          return { ok: false, error: error };
        }

        if (retryCount < maxRetries) {
          retryCount += 1;
          requestSync(Math.max(retryCount * 300, syncBlockedUntil > Date.now() ? syncBlockedUntil - Date.now() : 0));
          return { ok: false, error: error };
        }

        retryCount = 0;
        console.warn('[BXGY] Cart sync failed', error);
        return { ok: false, error: error };
      })
      .finally(function () {
        isSyncing = false;
        activeSyncPromise = null;

        if (syncQueued) {
          syncQueued = false;
          requestSync();
        }
      });

    return activeSyncPromise;
  }

  // Respect a short cooldown after rate limiting so we don't keep hammering /cart.js.
  function requestSync(delay) {
    var wait = typeof delay === 'number' ? delay : 120;
    if (syncBlockedUntil > Date.now()) {
      wait = Math.max(wait, syncBlockedUntil - Date.now());
    }

    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(function () {
      syncTimer = null;
      runSync();
    }, wait);
  }

  function isCartMutation(resource) {
    var url = typeof resource === 'string' ? resource : resource && resource.url;
    if (!url || url.indexOf(SOURCE) !== -1) return false;

    return url.indexOf('/cart/add') !== -1 || url.indexOf('/cart/update') !== -1 || url.indexOf('/cart/change') !== -1;
  }

  function shouldSkipSync(resource, config) {
    var url = typeof resource === 'string' ? resource : resource && resource.url;
    if (!url || url.indexOf('/cart/add') === -1) return false;

    return requestContainsBxgyItems(config);
  }

  function wrapFetch() {
    if (!window.fetch || window.__bxgyFetchWrapped) return;

    var originalFetch = window.fetch;
    window.__bxgyFetchWrapped = true;

    window.fetch = function (resource, config) {
      return originalFetch.apply(this, arguments).then(function (response) {
        if (response.ok && isCartMutation(resource) && !shouldSkipSync(resource, config)) {
          requestSync();
        }

        return response;
      });
    };
  }

  window.theme.bxgy = {
    getRules: getRules,
    findRulesForVariant: findRulesForVariant,
    findRuleForVariant: findRuleForVariant,
    decorateMainItem: decorateMainItem,
    buildGiftItems: buildGiftItems,
    buildGiftItem: buildGiftItem,
    validateGiftStockForRules: validateGiftStockForRules,
    syncCart: requestSync,
    syncCartNow: runSync,
    isBxgyGiftLine: isBxgyGiftLine,
    getLastCartErrorMessage: function () {
      return lastCartErrorMessage;
    }
  };

  wrapFetch();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      requestSync();
      scheduleRuleBoundarySync(getRules());
    });
  } else {
    requestSync();
    scheduleRuleBoundarySync(getRules());
  }
})();
